// live/LiveActionRamp.tsx (GH3) - the dynamic action ramp. Unlike the authored diagnose/ActionRamp
// (narrowed to fold/call/raise for its one teaching hand), this renders the TRUE legal option set from the
// engine (RampAction[] spanning check/call/fold/bet/raise/jam) - so the option set is always exactly what
// the rules allow, never a template (Joe's dynamic-option-set correctness guarantee). check/call/fold and
// jam commit immediately; bet/raise open the reused manual <BetMathBuilder>. Optional price gate (Solo).
import { useState } from 'react';
import type { RampAction } from '../engine/liveHand';
import type { BetMathDef } from '../diagnose/data';
import { BetMathBuilder } from '../diagnose/BetMathBuilder';

export interface LiveActionRampProps {
  actions: RampAction[];
  /** the street's manual-raise config (null when neither bet nor raise is legal, e.g. a river shove). */
  bet: BetMathDef | null;
  /** the read must be locked before any action unlocks. */
  locked: boolean;
  /** Solo also gates on the typed price; defaults true (Diagnose gates on the read alone). */
  priceReady?: boolean;
  /** the gate hint shown above the buttons. */
  hint?: string;
  /** commit an action. `amount` is present only for a built bet/raise. */
  onCommit: (key: RampAction['key'], amount?: string) => void;
}

export function LiveActionRamp({ actions, bet, locked, priceReady = true, hint, onCommit }: LiveActionRampProps) {
  const ready = locked && priceReady;
  // which aggressive key (bet | raise) opened the builder, so we commit the right kind.
  const [building, setBuilding] = useState<'bet' | 'raise' | null>(null);

  if (building && (!ready || !bet)) setBuilding(null);

  if (building && bet && ready) {
    return (
      <BetMathBuilder
        bet={bet}
        onCancel={() => setBuilding(null)}
        onConfirm={(amount) => onCommit(building, amount)}
      />
    );
  }

  const defaultHint = !locked ? 'Lock your read first, then choose your action.' : undefined;

  const handle = (a: RampAction) => {
    if (!ready) return;
    if ((a.key === 'bet' || a.key === 'raise') && bet) {
      setBuilding(a.key);
      return;
    }
    onCommit(a.key); // fold / check / call / jam commit straight away
  };

  return (
    <>
      {(hint || defaultHint) && <div className="lockhint">{hint ?? defaultHint}</div>}
      <div className="acts">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={'btn ' + a.cls}
            disabled={!ready}
            onClick={() => handle(a)}
          >
            {a.label}
            <s>{a.sub}</s>
          </button>
        ))}
      </div>
    </>
  );
}
